"use client";

import React, { useState } from "react";
import { useTranslations } from "next-intl";
import { Check, ChevronsUpDown, Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { INSURANCE_TYPE_ICONS } from "@/components/resource/insurance/insurance-type-meta";
import { insuranceTypes, type InsuranceType } from "@/lib/data/insurance";
import {
  insuranceSearchSort,
  type InsuranceSearchParams,
  type InsuranceSearchSort,
} from "@/lib/fetch/insurance";

type Props = {
  form: InsuranceSearchParams;
  setForm: (form: InsuranceSearchParams) => void;
  /** Published products per insurance type; types at zero are not offered. */
  typeCounts: Record<InsuranceType, number>;
};

/** Case- and accent-insensitive matching, so "reis" finds "Reisverzekering". */
const normalize = (value: string) =>
  value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

/**
 * Provider search filters on one row: the free-text query, the insurance
 * type and the sort. The type control is a searchable dropdown over one flat
 * list of the offered types (most products first), because a 42-entry select
 * is unusable and grouping the types under family tabs read as duplicates.
 */
const InsuranceSearchForm: React.FC<Props> = ({ form, setForm, typeCounts }) => {
  const t = useTranslations("insurance");
  const selectedType = form.type === "ALL" ? null : form.type;

  const [open, setOpen] = useState(false);
  const [typeQuery, setTypeQuery] = useState("");

  const labelOf = (type: InsuranceType) => t(`type.${type}`);
  const query = normalize(typeQuery.trim());

  const options = insuranceTypes
    .filter((type) => typeCounts[type] > 0)
    .filter((type) => !query || normalize(labelOf(type)).includes(query))
    .sort(
      (left, right) =>
        typeCounts[right] - typeCounts[left] ||
        labelOf(left).localeCompare(labelOf(right)),
    );

  const totalProducts = insuranceTypes.reduce(
    (sum, type) => sum + typeCounts[type],
    0,
  );

  const selectType = (type: InsuranceType | "ALL") => {
    setForm({ ...form, type });
    setOpen(false);
    setTypeQuery("");
  };

  const SelectedIcon = selectedType ? INSURANCE_TYPE_ICONS[selectedType] : null;

  return (
    <div className="flex w-full flex-col gap-4 rounded-2xl border border-border bg-background p-5 shadow-sm sm:p-6">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,280px)_minmax(0,200px)]">
        <div className="flex flex-col gap-2">
          <Label htmlFor="search">{t("search.form.search.label")}</Label>
          <div className="relative w-full">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="search"
              type="text"
              placeholder={t("search.form.search.placeholder")}
              value={form.search || ""}
              onChange={(event) =>
                setForm({ ...form, search: event.target.value })
              }
              className="h-11 w-full pl-10 pr-10 text-sm"
            />
            {form.search && (
              <button
                type="button"
                onClick={() => setForm({ ...form, search: "" })}
                aria-label={t("search.form.search.clear")}
                className="absolute right-2 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <X className="size-4" />
              </button>
            )}
          </div>
        </div>

        <div className="flex min-w-0 flex-col gap-2">
          <Label htmlFor="type">{t("search.form.type.label")}</Label>
          <Popover
            open={open}
            onOpenChange={(next) => {
              setOpen(next);
              if (!next) setTypeQuery("");
            }}
          >
            <PopoverTrigger asChild>
              <button
                id="type"
                type="button"
                role="combobox"
                aria-expanded={open}
                aria-controls="insurance-type-options"
                className="flex h-11 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 text-sm font-medium ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                <span className="flex min-w-0 items-center gap-2">
                  {SelectedIcon && selectedType ? (
                    <>
                      <span className="flex size-6 flex-none items-center justify-center rounded-md bg-secondary">
                        <SelectedIcon className="size-3.5" />
                      </span>
                      <span className="truncate">{labelOf(selectedType)}</span>
                      <span className="flex-none rounded-full bg-muted px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground">
                        {typeCounts[selectedType]}
                      </span>
                    </>
                  ) : (
                    <span className="truncate text-muted-foreground">
                      {t("search.form.type.all")}
                    </span>
                  )}
                </span>
                <ChevronsUpDown className="size-4 flex-none opacity-50" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="w-[var(--radix-popover-trigger-width)] min-w-[280px] p-0"
            >
              <div className="border-b border-border p-2">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    autoFocus
                    type="text"
                    value={typeQuery}
                    onChange={(event) => setTypeQuery(event.target.value)}
                    placeholder={t("search.form.type.filter-placeholder")}
                    aria-label={t("search.form.type.filter-placeholder")}
                    className="h-9 w-full pl-9 text-sm"
                  />
                </div>
              </div>
              <ul
                id="insurance-type-options"
                role="listbox"
                className="max-h-72 overflow-y-auto p-1"
              >
                {!query && (
                  <li role="option" aria-selected={!selectedType}>
                    <button
                      type="button"
                      onClick={() => selectType("ALL")}
                      className={`flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-muted ${
                        !selectedType ? "font-semibold" : ""
                      }`}
                    >
                      <span className="truncate">
                        {t("search.form.type.all")}
                      </span>
                      <span className="ml-auto flex-none rounded-full bg-muted px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground">
                        {totalProducts}
                      </span>
                      {!selectedType && <Check className="size-4 flex-none" />}
                    </button>
                  </li>
                )}
                {options.map((type) => {
                  const Icon = INSURANCE_TYPE_ICONS[type];
                  const active = selectedType === type;

                  return (
                    <li key={type} role="option" aria-selected={active}>
                      <button
                        type="button"
                        onClick={() => selectType(type)}
                        className={`flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-muted ${
                          active ? "bg-secondary/60 font-semibold" : ""
                        }`}
                      >
                        <span className="flex size-6 flex-none items-center justify-center rounded-md bg-secondary">
                          <Icon className="size-3.5" />
                        </span>
                        <span className="truncate">{labelOf(type)}</span>
                        <span className="ml-auto flex-none rounded-full bg-muted px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground">
                          {typeCounts[type]}
                        </span>
                        {active && <Check className="size-4 flex-none" />}
                      </button>
                    </li>
                  );
                })}
                {options.length === 0 && (
                  <li className="px-3 py-6 text-center text-sm text-muted-foreground">
                    {t("search.form.type.no-match", { query: typeQuery.trim() })}
                  </li>
                )}
              </ul>
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex min-w-0 flex-col gap-2">
          <Label htmlFor="sort">{t("search.form.sort.label")}</Label>
          <Select
            value={form.sort}
            onValueChange={(next) =>
              setForm({ ...form, sort: next as InsuranceSearchSort })
            }
          >
            <SelectTrigger id="sort" className="h-11 w-full text-sm font-medium">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {insuranceSearchSort.map((option) => (
                  <SelectItem key={option} value={option}>
                    {t(`search.sort.${option}`)}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

export default InsuranceSearchForm;
